import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  AlertCircle,
  UploadCloud,
  RefreshCw,
  Trash2,
  FileCheck,
  FileText,
  ExternalLink,
  Calendar,
  Clock,
  ShieldCheck,
  Tag,
  BookOpen,
} from "lucide-react";
import { fixMojibake } from "../../utils/downLoadUtils";
import { ConfirmModal } from "../base/BaseModal";
import VietnameseDatePicker from "./VietnameseDatePicker";
import ManageableDocTypeSelect from "./ManageableDocTypeSelect";
import CustomSelect from "../base/CustomSelect";

const PHAM_VI_OPTIONS = [
  { value: "CONG_KHAI", label: "Công khai" },
  { value: "NOI_BO", label: "Nội bộ" },
];

export const DEFAULT_LEGAL_DOC_TYPES = [];

export default function LegalDocumentFormModal({
  onBack,
  onSubmit,
  onSaveDraft,
  docTypes = [],
  issuingAgencies = [],
  isLoading = false,
  initialData = null,
  isEditMode = false,
}) {
  const availableDocTypes = docTypes && docTypes.length > 0 ? docTypes : DEFAULT_LEGAL_DOC_TYPES;

  const [formData, setFormData] = useState({
    soHieu: "",
    tieuDe: "",
    idDanhMuc: "",
    coQuanBanHanh: "",
    ngayBanHanh: "",
    ngayHieuLuc: "",
    ngayHetHan: "",
    phamVi: "CONG_KHAI",
    moTa: "",
    tags: "",
  });

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const [file, setFile] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingFiles, setExistingFiles] = useState([]);
  const [tagInput, setTagInput] = useState("");
  
  const [banHanhDisplay, setBanHanhDisplay] = useState("");
  const [hieuLucDisplay, setHieuLucDisplay] = useState("");
  const [hetHanDisplay, setHetHanDisplay] = useState("");

  const fileInputRef = useRef(null);
  const banHanhInputRef = useRef(null);
  const hieuLucInputRef = useRef(null);
  const hetHanInputRef = useRef(null);

  const hasUnsavedDataRef = useRef(false);
  const splitContainerRef = useRef(null);
  const isDragging = useRef(false);
  const [leftWidth, setLeftWidth] = useState(50);

  useEffect(() => {
    if (isEditMode && initialData) {
      const bh = initialData.ngay_ban_hanh ? initialData.ngay_ban_hanh.split("T")[0] : "";
      const hl = initialData.ngay_hieu_luc ? initialData.ngay_hieu_luc.split("T")[0] : "";
      const hh = initialData.ngay_het_han ? initialData.ngay_het_han.split("T")[0] : "";

      setFormData({
        soHieu: initialData.so_hieu || "",
        tieuDe: initialData.tieu_de || "",
        idDanhMuc: initialData.thu_vien_danh_muc?.id || "",
        coQuanBanHanh: initialData.co_quan_ban_hanh || "",
        ngayBanHanh: bh,
        ngayHieuLuc: hl,
        ngayHetHan: hh,
        phamVi: initialData.pham_vi || "CONG_KHAI",
        moTa: initialData.mo_ta || "",
        tags:
          initialData.thu_vien_tai_lieu_tag
            ?.map((t) => t.thu_vien_tag?.ten)
            .join(", ") || "",
      });
      setBanHanhDisplay(bh ? bh.split("-").reverse().join("/") : "");
      setHieuLucDisplay(hl ? hl.split("-").reverse().join("/") : "");
      setHetHanDisplay(hh ? hh.split("-").reverse().join("/") : "");
      setExistingFiles(initialData.thu_vien_tai_lieu_file || []);
    }
  }, [isEditMode, initialData]);

  // Live preview data
  const previewData = useMemo(() => {
    const selectedDocType = availableDocTypes.find((cat) => cat.id === formData.idDanhMuc);
    return {
      soHieu: formData.soHieu.trim() || "Chưa có số hiệu",
      tieuDe: formData.tieuDe.trim() || "Chưa có tiêu đề văn bản",
      coQuanBanHanh: formData.coQuanBanHanh.trim() || "Cơ quan ban hành",
      docTypeName: selectedDocType?.name || "Loại văn bản",
      moTa: formData.moTa.trim(),
      tags: formData.tags
        ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : [],
    };
  }, [formData, availableDocTypes]);

  const getFullFileUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("blob:") || path.startsWith("data:") || path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    const base = (process.env.REACT_APP_API_URL || "http://localhost:8880").replace(/\/+$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${cleanPath}`;
  };

  const pdfPreviewUrl = useMemo(() => {
    if (file) {
      const isPdf =
        file.type === "application/pdf" ||
        file.name?.toLowerCase().endsWith(".pdf");
      if (isPdf) {
        return URL.createObjectURL(file);
      }
    } else if (existingFiles.length > 0) {
      const f = existingFiles[0];
      const path = f.duong_dan || "";
      if (
        path.toLowerCase().endsWith(".pdf") ||
        f.ten_file?.toLowerCase().endsWith(".pdf")
      ) {
        return getFullFileUrl(path);
      }
    }
    return null;
  }, [file, existingFiles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    hasUnsavedDataRef.current = true;
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const parseDateString = (str) => {
    if (str.length === 10) {
      const parts = str.split("/");
      if (parts.length === 3) {
        const [d, m, y] = parts;
        if (d && m && y && d.length === 2 && m.length === 2 && y.length === 4) {
          return `${y}-${m}-${d}`;
        }
      }
    }
    return "";
  };

  const handleCustomDateChange = (e, fieldName, setDisplay) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^0-9/]/g, "");
    setDisplay(cleaned);
    const parsed = parseDateString(cleaned);
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: parsed };
      if (fieldName === "ngayHieuLuc" && !parsed) {
        next.ngayHetHan = "";
        setHetHanDisplay("");
      }
      return next;
    });
    hasUnsavedDataRef.current = true;
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  const handleBlur = (fieldName) => {
    let errorMsg = null;
    if (fieldName === "soHieu") {
      if (!formData.soHieu.trim()) {
        errorMsg = "Vui lòng nhập số hiệu văn bản";
      } else if (formData.soHieu.length > 50) {
        errorMsg = "Số hiệu không được vượt quá 50 ký tự";
      }
    } else if (fieldName === "idDanhMuc") {
      if (!formData.idDanhMuc) {
        errorMsg = "Vui lòng chọn loại văn bản";
      }
    } else if (fieldName === "tieuDe") {
      if (!formData.tieuDe.trim()) {
        errorMsg = "Vui lòng nhập tiêu đề văn bản";
      } else if (formData.tieuDe.length > 300) {
        errorMsg = "Tiêu đề không được vượt quá 300 ký tự";
      }
    } else if (fieldName === "coQuanBanHanh") {
      if (!formData.coQuanBanHanh.trim()) {
        errorMsg = "Vui lòng nhập cơ quan ban hành";
      } else if (formData.coQuanBanHanh.length > 50) {
        errorMsg = "Cơ quan ban hành không được vượt quá 50 ký tự";
      }
    } else if (fieldName === "ngayBanHanh") {
      const todayStr = new Date().toISOString().split("T")[0];
      if (!formData.ngayBanHanh) {
        errorMsg = "Vui lòng chọn ngày ban hành";
      } else if (formData.ngayBanHanh < "1945-01-01") {
        errorMsg = "Ngày ban hành không được trước năm 1945";
      } else if (formData.ngayBanHanh > todayStr) {
        errorMsg = "Ngày ban hành không được vượt quá ngày hiện tại";
      }
    } else if (fieldName === "ngayHieuLuc") {
      if (formData.ngayBanHanh && formData.ngayHieuLuc && formData.ngayHieuLuc < formData.ngayBanHanh) {
        errorMsg = "Ngày hiệu lực không được trước ngày ban hành";
      }
    } else if (fieldName === "ngayHetHan") {
      if (formData.ngayHieuLuc && formData.ngayHetHan && formData.ngayHetHan < formData.ngayHieuLuc) {
        errorMsg = "Ngày hết hạn không được trước ngày hiệu lực";
      }
    }
    setErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const tagsArray = formData.tags
    ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = tagInput.trim().slice(0, 20);
      if (!val) return;
      if (tagsArray.length >= 10) return;
      if (tagsArray.some((t) => t.toLowerCase() === val.toLowerCase())) {
        setTagInput("");
        return;
      }
      const updated = [...tagsArray, val].join(", ");
      setFormData((prev) => ({ ...prev, tags: updated }));
      setTagInput("");
      hasUnsavedDataRef.current = true;
    } else if (e.key === "Backspace" && !tagInput && tagsArray.length > 0) {
      const updated = tagsArray.slice(0, -1).join(", ");
      setFormData((prev) => ({ ...prev, tags: updated }));
      hasUnsavedDataRef.current = true;
    }
  };

  const removeTag = (index) => {
    const updated = tagsArray.filter((_, i) => i !== index).join(", ");
    setFormData((prev) => ({ ...prev, tags: updated }));
    hasUnsavedDataRef.current = true;
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const isPdf = selected.type === "application/pdf" || selected.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setErrors((prev) => ({ ...prev, file: "Chỉ chấp nhận file định dạng PDF" }));
        return;
      }
      if (selected.size > 20 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: "File không được vượt quá 20MB" }));
        return;
      }
      setFile(selected);
      hasUnsavedDataRef.current = true;
      setErrors((prev) => ({ ...prev, file: null }));
    }
  };

  const handleFileDrop = (droppedFile) => {
    if (droppedFile) {
      const isPdf = droppedFile.type === "application/pdf" || droppedFile.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setErrors((prev) => ({ ...prev, file: "Chỉ chấp nhận file định dạng PDF" }));
        return;
      }
      if (droppedFile.size > 20 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: "File không được vượt quá 20MB" }));
        return;
      }
      setFile(droppedFile);
      hasUnsavedDataRef.current = true;
      setErrors((prev) => ({ ...prev, file: null }));
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.soHieu.trim()) {
      newErrors.soHieu = "Vui lòng nhập số hiệu văn bản";
    } else if (formData.soHieu.length > 50) {
      newErrors.soHieu = "Số hiệu không được vượt quá 50 ký tự";
    }

    if (!formData.idDanhMuc) {
      newErrors.idDanhMuc = "Vui lòng chọn loại văn bản";
    }

    if (!formData.tieuDe.trim()) {
      newErrors.tieuDe = "Vui lòng nhập tiêu đề văn bản";
    } else if (formData.tieuDe.length > 300) {
      newErrors.tieuDe = "Tiêu đề không được vượt quá 300 ký tự";
    }

    if (!formData.coQuanBanHanh.trim()) {
      newErrors.coQuanBanHanh = "Vui lòng nhập cơ quan ban hành";
    } else if (formData.coQuanBanHanh.length > 50) {
      newErrors.coQuanBanHanh = "Cơ quan ban hành không được vượt quá 50 ký tự";
    }

    const todayStr = new Date().toISOString().split("T")[0];
    if (!formData.ngayBanHanh) {
      newErrors.ngayBanHanh = "Vui lòng chọn ngày ban hành";
    } else if (formData.ngayBanHanh < "1945-01-01") {
      newErrors.ngayBanHanh = "Ngày ban hành không được trước năm 1945";
    } else if (formData.ngayBanHanh > todayStr) {
      newErrors.ngayBanHanh = "Ngày ban hành không được vượt quá ngày hiện tại";
    }

    if (!isEditMode && !file) {
      newErrors.file = "Vui lòng đính kèm file tài liệu (PDF/DOC/DOCX)";
    } else if (isEditMode && !file && existingFiles.length === 0) {
      newErrors.file = "Vui lòng đính kèm file tài liệu (PDF/DOC/DOCX)";
    }

    if (formData.ngayBanHanh && formData.ngayHieuLuc && formData.ngayHieuLuc < formData.ngayBanHanh) {
      newErrors.ngayHieuLuc = "Ngày hiệu lực không được trước ngày ban hành";
    }

    if (formData.ngayHieuLuc && formData.ngayHetHan && formData.ngayHetHan < formData.ngayHieuLuc) {
      newErrors.ngayHetHan = "Ngày hết hạn không được trước ngày hiệu lực";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const fd = new FormData();
    fd.append("soHieu", formData.soHieu.trim());
    fd.append("tieuDe", formData.tieuDe.trim());
    fd.append("idDanhMuc", formData.idDanhMuc);
    fd.append("coQuanBanHanh", formData.coQuanBanHanh.trim());
    fd.append("ngayBanHanh", formData.ngayBanHanh);
    if (formData.ngayHieuLuc) fd.append("ngayHieuLuc", formData.ngayHieuLuc);
    if (formData.ngayHetHan) fd.append("ngayHetHan", formData.ngayHetHan);
    fd.append("phamVi", formData.phamVi);
    if (!isEditMode) {
      fd.append("trangThai", "CHO_DUYET");
    }
    if (formData.moTa.trim()) fd.append("moTa", formData.moTa.trim());
    if (formData.tags.trim()) fd.append("tags", formData.tags.trim());
    if (file) fd.append("file", file);

    onSubmit(fd);
  };

  const handleSaveDraft = () => {
    if (!validate()) return;

    const fd = new FormData();
    fd.append("tieuDe", formData.tieuDe.trim() || "Chưa có tiêu đề");
    if (formData.soHieu.trim()) fd.append("soHieu", formData.soHieu.trim());
    if (formData.idDanhMuc) fd.append("idDanhMuc", formData.idDanhMuc);
    if (formData.coQuanBanHanh.trim()) fd.append("coQuanBanHanh", formData.coQuanBanHanh.trim());
    if (formData.ngayBanHanh) fd.append("ngayBanHanh", formData.ngayBanHanh);
    if (formData.ngayHieuLuc) fd.append("ngayHieuLuc", formData.ngayHieuLuc);
    if (formData.ngayHetHan) fd.append("ngayHetHan", formData.ngayHetHan);
    fd.append("phamVi", formData.phamVi);
    if (!isEditMode) {
      fd.append("trangThai", "NHAP");
    }
    if (formData.moTa.trim()) fd.append("moTa", formData.moTa.trim());
    if (formData.tags.trim()) fd.append("tags", formData.tags.trim());
    if (file) fd.append("file", file);

    onSaveDraft(fd);
  };

  const handleBack = () => {
    if (hasUnsavedDataRef.current) {
      setShowLeaveConfirm(true);
      return;
    }
    onBack();
  };

  // Draggable split pane
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      setLeftWidth(Math.max(30, Math.min(70, pct)));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Quay lại
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {isEditMode ? "Chỉnh sửa văn bản quy phạm pháp luật" : "Thêm văn bản quy phạm pháp luật mới"}
          </h2>
          <p className="text-sm text-gray-500">Quy phạm pháp luật</p>
        </div>
      </div>

      {/* Body: 2-column layout */}
      <div className="p-6">
        <div ref={splitContainerRef} className="flex gap-0">
          {/* ===== LEFT COLUMN: Form ===== */}
          <div style={{ width: `${leftWidth}%` }} className="space-y-4 max-h-[65vh] overflow-y-auto pl-1 pr-3 py-1 flex-shrink-0">
            
            {/* Rejection Alert Banner */}
            {(initialData?.trang_thai === "TU_CHOI" || initialData?.ly_do_tu_choi) && (
              <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 flex items-start gap-3 shadow-xs">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-red-900 block mb-0.5">Lý do từ chối:</span>
                  <span className="text-red-800 leading-relaxed">{initialData.ly_do_tu_choi || "Vui lòng kiểm tra và hoàn thiện lại thông tin tài liệu"}</span>
                </div>
              </div>
            )}

            {/* Số hiệu & Loại văn bản */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Số hiệu văn bản <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="soHieu"
                  value={formData.soHieu}
                  maxLength={50}
                  onChange={handleChange}
                  onBlur={() => handleBlur("soHieu")}
                  placeholder="VD: 12/2025/QĐ-UBND"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                    errors.soHieu || formData.soHieu.length >= 50
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />
                <div className="flex justify-between items-center text-xs mt-1">
                  {errors.soHieu ? (
                    <p className="text-red-500">{errors.soHieu}</p>
                  ) : formData.soHieu.length >= 50 ? (
                    <p className="text-red-500 font-medium">Đã đạt tối đa 50 ký tự</p>
                  ) : (
                    <span />
                  )}
                  <span className={`ml-auto pl-2 ${formData.soHieu.length >= 50 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {formData.soHieu.length}/50
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Loại văn bản <span className="text-red-500">*</span>
                </label>
                <ManageableDocTypeSelect
                  name="idDanhMuc"
                  value={formData.idDanhMuc}
                  onChange={handleChange}
                  onBlur={() => handleBlur("idDanhMuc")}
                  initialDocTypes={availableDocTypes}
                  error={errors.idDanhMuc}
                  placeholder="-- Chọn loại văn bản --"
                />
              </div>
            </div>

            {/* Tiêu đề */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Tiêu đề văn bản <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="tieuDe"
                value={formData.tieuDe}
                maxLength={300}
                onChange={handleChange}
                onBlur={() => handleBlur("tieuDe")}
                placeholder="Nhập tiêu đề hoặc tên văn bản..."
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                  errors.tieuDe || formData.tieuDe.length >= 300
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
              <div className="flex justify-between items-center text-xs mt-1">
                {errors.tieuDe ? (
                  <p className="text-red-500">{errors.tieuDe}</p>
                ) : formData.tieuDe.length >= 300 ? (
                  <p className="text-red-500 font-medium">Đã đạt giới hạn tối đa 300 ký tự</p>
                ) : (
                  <span />
                )}
                <span className={`ml-auto pl-2 ${formData.tieuDe.length >= 300 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {formData.tieuDe.length}/300
                </span>
              </div>
            </div>

            {/* Cơ quan ban hành & Phạm vi */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Cơ quan ban hành <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="coQuanBanHanh"
                  value={formData.coQuanBanHanh}
                  maxLength={50}
                  onChange={handleChange}
                  onBlur={() => handleBlur("coQuanBanHanh")}
                  placeholder="VD: UBND Thành phố Hồ Chí Minh"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                    errors.coQuanBanHanh || formData.coQuanBanHanh.length >= 50
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                />
                <div className="flex justify-between items-center text-xs mt-1">
                  {errors.coQuanBanHanh ? (
                    <p className="text-red-500">{errors.coQuanBanHanh}</p>
                  ) : formData.coQuanBanHanh.length >= 50 ? (
                    <p className="text-red-500 font-medium">Đã đạt tối đa 50 ký tự</p>
                  ) : (
                    <span />
                  )}
                  <span className={`ml-auto pl-2 ${formData.coQuanBanHanh.length >= 50 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {formData.coQuanBanHanh.length}/50
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Phạm vi áp dụng</label>
                <CustomSelect
                  name="phamVi"
                  value={formData.phamVi}
                  onChange={handleChange}
                  options={PHAM_VI_OPTIONS}
                />
              </div>
            </div>

            {/* 3 Dates: Ngày ban hành, Ngày hiệu lực, Ngày hết hạn */}
            <div className="grid grid-cols-3 gap-3">
              {/* Ngày ban hành */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Ngày ban hành <span className="text-red-500">*</span>
                </label>
                <VietnameseDatePicker
                  value={formData.ngayBanHanh}
                  minDate="1945-01-01"
                  maxDate={new Date().toISOString().split("T")[0]}
                  onChange={(isoVal, displayVal) => {
                    setFormData((prev) => ({ ...prev, ngayBanHanh: isoVal }));
                    setBanHanhDisplay(displayVal);
                    if (errors.ngayBanHanh) setErrors((prev) => ({ ...prev, ngayBanHanh: null }));
                    if (formData.ngayHieuLuc && isoVal && formData.ngayHieuLuc < isoVal) {
                      setErrors((prev) => ({ ...prev, ngayHieuLuc: "Ngày hiệu lực không được trước ngày ban hành" }));
                    } else if (errors.ngayHieuLuc) {
                      setErrors((prev) => ({ ...prev, ngayHieuLuc: null }));
                    }
                    hasUnsavedDataRef.current = true;
                  }}
                  onBlur={() => handleBlur("ngayBanHanh")}
                  error={!!errors.ngayBanHanh}
                  placeholder="dd/MM/yyyy"
                />
                {errors.ngayBanHanh && <p className="text-red-500 text-xs mt-1">{errors.ngayBanHanh}</p>}
              </div>

              {/* Ngày hiệu lực */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Ngày hiệu lực</label>
                <VietnameseDatePicker
                  value={formData.ngayHieuLuc}
                  minDate={formData.ngayBanHanh || undefined}
                  align="right"
                  onChange={(isoVal, displayVal) => {
                    setFormData((prev) => {
                      const next = { ...prev, ngayHieuLuc: isoVal };
                      if (!isoVal) {
                        next.ngayHetHan = "";
                        setHetHanDisplay("");
                      }
                      return next;
                    });
                    setHieuLucDisplay(displayVal);
                    if (formData.ngayBanHanh && isoVal && isoVal < formData.ngayBanHanh) {
                      setErrors((prev) => ({ ...prev, ngayHieuLuc: "Ngày hiệu lực không được trước ngày ban hành" }));
                    } else {
                      setErrors((prev) => ({ ...prev, ngayHieuLuc: null }));
                    }
                    if (formData.ngayHetHan && isoVal && formData.ngayHetHan < isoVal) {
                      setErrors((prev) => ({ ...prev, ngayHetHan: "Ngày hết hạn không được trước ngày hiệu lực" }));
                    } else if (errors.ngayHetHan) {
                      setErrors((prev) => ({ ...prev, ngayHetHan: null }));
                    }
                    hasUnsavedDataRef.current = true;
                  }}
                  onBlur={() => handleBlur("ngayHieuLuc")}
                  error={!!errors.ngayHieuLuc}
                  placeholder="dd/MM/yyyy"
                />
                {errors.ngayHieuLuc && <p className="text-red-500 text-xs mt-1">{errors.ngayHieuLuc}</p>}
              </div>

              {/* Ngày hết hạn */}
              <div>
                <label className={`block text-sm font-bold mb-1 transition-colors ${!formData.ngayHieuLuc ? "text-gray-400" : "text-gray-700"}`}>
                  Ngày hết hạn
                </label>
                <VietnameseDatePicker
                  value={formData.ngayHetHan}
                  minDate={formData.ngayHieuLuc || undefined}
                  disabled={!formData.ngayHieuLuc}
                  align="right"
                  onChange={(isoVal, displayVal) => {
                    setFormData((prev) => ({ ...prev, ngayHetHan: isoVal }));
                    setHetHanDisplay(displayVal);
                    if (formData.ngayHieuLuc && isoVal && isoVal < formData.ngayHieuLuc) {
                      setErrors((prev) => ({ ...prev, ngayHetHan: "Ngày hết hạn không được trước ngày hiệu lực" }));
                    } else {
                      setErrors((prev) => ({ ...prev, ngayHetHan: null }));
                    }
                    hasUnsavedDataRef.current = true;
                  }}
                  onBlur={() => handleBlur("ngayHetHan")}
                  error={!!errors.ngayHetHan}
                  placeholder="dd/MM/yyyy"
                />
                {errors.ngayHetHan && <p className="text-red-500 text-xs mt-1">{errors.ngayHetHan}</p>}
              </div>
            </div>

            {/* Trích yếu / Mô tả */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Trích yếu nội dung / Mô tả</label>
              <textarea
                name="moTa"
                value={formData.moTa}
                maxLength={300}
                onChange={handleChange}
                rows={4}
                placeholder="Nhập tóm tắt hoặc trích yếu nội dung văn bản..."
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500 resize-none ${
                  formData.moTa.length >= 300
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
              <div className="flex justify-between items-center text-xs mt-1">
                {formData.moTa.length >= 300 ? (
                  <p className="text-red-500 font-medium">Đã đạt tối đa 300 ký tự</p>
                ) : (
                  <span />
                )}
                <span className={`ml-auto pl-2 ${formData.moTa.length >= 300 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {formData.moTa.length}/300
                </span>
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-bold text-gray-700">Tags / Từ khóa</label>
                <span className={`text-xs ${tagsArray.length >= 10 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                  {tagsArray.length}/10 tag
                </span>
              </div>
              <div
                className={`flex flex-wrap items-center gap-1.5 px-3 py-2 border rounded-lg text-sm bg-white min-h-[42px] cursor-text transition-colors ${
                  tagsArray.length >= 10
                    ? "border-gray-300 focus-within:ring-2 focus-within:ring-red-400"
                    : "border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                }`}
                onClick={() => document.getElementById("legal-tag-input")?.focus()}
              >
                {tagsArray.map((tag, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(idx);
                      }}
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                <input
                  id="legal-tag-input"
                  type="text"
                  maxLength={20}
                  disabled={tagsArray.length >= 10}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={
                    tagsArray.length >= 10
                      ? "Đã đạt tối đa 10 tag"
                      : tagsArray.length === 0
                      ? "Nhập tag (tối đa 20 ký tự) và nhấn Enter..."
                      : "Thêm tag..."
                  }
                  className="flex-1 min-w-[120px] border-none outline-none text-sm bg-transparent p-0 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex justify-between items-center text-xs text-gray-400 mt-1">
                <span>
                  {tagsArray.length >= 10 ? (
                    <span className="text-red-500 font-medium">Đã đạt giới hạn tối đa 10 tag</span>
                  ) : tagInput.length >= 20 ? (
                    <span className="text-red-500 font-medium">Đã đạt tối đa 20 ký tự cho tag này</span>
                  ) : (
                    "Tối đa 10 tag, mỗi tag tối đa 20 ký tự (Gõ tag và nhấn Enter để thêm)"
                  )}
                </span>
                {tagInput && tagsArray.length < 10 && (
                  <span className={`ml-auto pl-2 ${tagInput.length >= 20 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {tagInput.length}/20 ký tự
                  </span>
                )}
              </div>
            </div>

            {/* File tài liệu */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                Tệp đính kèm (PDF) {!isEditMode && <span className="text-red-500">*</span>}
              </label>

              {!file && existingFiles.length === 0 ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingFile(true);
                  }}
                  onDragLeave={() => setIsDraggingFile(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingFile(false);
                    if (e.dataTransfer.files?.[0]) {
                      handleFileDrop(e.dataTransfer.files[0]);
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    isDraggingFile
                      ? "border-blue-500 bg-blue-50/70 ring-2 ring-blue-200"
                      : "border-gray-300 hover:border-blue-400 hover:bg-slate-50/70 bg-white"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3 shadow-xs">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    Kéo & thả file PDF vào đây, hoặc{" "}
                    <span className="text-blue-600 hover:underline font-normal">chọn từ máy tính</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Hỗ trợ định dạng: <span className="font-semibold text-gray-700">PDF</span> (Tối đa 20MB)
                  </p>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-blue-50/60 to-indigo-50/40 border border-blue-200/80 rounded-xl p-4 shadow-xs">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-11 h-11 bg-red-100 text-red-700 rounded-xl flex items-center justify-center font-bold text-xs uppercase flex-shrink-0 shadow-xs border border-red-200">
                        {file?.name ? file.name.split(".").pop() : "PDF"}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-gray-900 truncate max-w-[280px]">
                            {file?.name || fixMojibake(existingFiles[0]?.ten_file)}
                          </p>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex-shrink-0">
                            <FileCheck className="w-3 h-3" />
                            Đã sẵn sàng
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Dung lượng:{" "}
                          {file
                            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                            : existingFiles[0]?.kich_thuoc_mb
                              ? `${existingFiles[0]?.kich_thuoc_mb} MB`
                              : "---"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-white hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Đổi file
                      </button>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                        title="Xóa file"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
              {errors.file && <p className="text-red-500 text-xs mt-1.5">{errors.file}</p>}
            </div>

          </div>

          {/* ===== DRAG HANDLE ===== */}
          <div
            className="flex-shrink-0 w-2 cursor-col-resize relative group flex items-center justify-center"
            onMouseDown={(e) => {
              isDragging.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
              e.preventDefault();
            }}
          >
            <div className="w-1 h-full bg-gray-200 group-hover:bg-blue-400 group-active:bg-blue-500 transition-colors rounded-full" />
          </div>

          {/* ===== RIGHT COLUMN: Live Preview ===== */}
          <div style={{ flex: 1 }} className="bg-slate-50 rounded-xl overflow-hidden border border-gray-200 max-h-[65vh] overflow-y-auto min-w-0 p-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
              {/* Header preview: Quốc hiệu & Cơ quan ban hành & Số hiệu */}
              <div className="border-b border-gray-200 pb-4 flex justify-between items-start gap-3">
                <div className="max-w-[55%] space-y-0.5">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-tight break-words [overflow-wrap:anywhere]">
                    {previewData.coQuanBanHanh}
                  </p>
                  <p className="text-xs text-gray-700 font-medium break-words [overflow-wrap:anywhere]">
                    Số: <span className="font-bold text-blue-700">{previewData.soHieu}</span>
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-tight">
                    CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
                  </p>
                  <p className="text-xs font-semibold text-gray-700 underline underline-offset-4 decoration-gray-400 mt-0.5">
                    Độc lập - Tự do - Hạnh phúc
                  </p>
                </div>
              </div>

              {/* Tiêu đề văn bản */}
              <h1 className="text-base sm:text-lg font-bold text-gray-900 uppercase leading-snug text-center break-words [overflow-wrap:anywhere] pt-1">
                {previewData.tieuDe}
              </h1>

              {/* Bảng mốc thời gian 3 cột */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/90 px-5 py-3 rounded-xl border border-slate-200/80 text-xs">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <span className="text-gray-500 block text-[11px]">Ngày ban hành</span>
                    <span className="font-semibold text-gray-800">{banHanhDisplay || "---"}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <div>
                    <span className="text-gray-500 block text-[11px]">Ngày hiệu lực</span>
                    <span className="font-semibold text-gray-800">{hieuLucDisplay || "---"}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div>
                    <span className="text-gray-500 block text-[11px]">Ngày hết hạn</span>
                    <span className="font-semibold text-gray-800">{hetHanDisplay || "Còn hiệu lực"}</span>
                  </div>
                </div>
              </div>

              {/* Trích yếu nội dung */}
              <div>
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-blue-600" />
                  Trích yếu nội dung
                </h3>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/80 min-h-[60px]">
                  {previewData.moTa || "Chưa có trích yếu nội dung văn bản..."}
                </p>
              </div>

              {/* Từ khóa liên quan (Tags) */}
              {previewData.tags.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-blue-600" />
                    Từ khóa liên quan
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {previewData.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Khung xem trước tài liệu PDF & Tệp đính kèm */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between gap-3 mb-2.5">
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    Xem trước tài liệu văn bản
                  </h3>
                  {pdfPreviewUrl && (
                    <a
                      href={pdfPreviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-xs"
                      title="Mở toàn màn hình trong tab mới"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Mở rộng
                    </a>
                  )}
                </div>

                {pdfPreviewUrl ? (
                  <div className="border border-slate-300 rounded-xl overflow-hidden shadow-xs bg-slate-800">
                    <iframe
                      src={`${pdfPreviewUrl}#toolbar=1&navpanes=0`}
                      className="w-full h-[480px] border-0 bg-white"
                      title="PDF Live Preview"
                    />
                  </div>
                ) : file || existingFiles.length > 0 ? (
                  <div className="bg-gradient-to-r from-blue-50/70 to-slate-50 border border-blue-200/80 rounded-xl p-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-xl flex items-center justify-center font-bold text-xs uppercase flex-shrink-0 border border-blue-200">
                        {file?.name?.split(".").pop() || existingFiles[0]?.ten_file?.split(".").pop() || "DOC"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-900 truncate">
                          {file?.name || fixMojibake(existingFiles[0]?.ten_file) || "Tài liệu văn bản"}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Dung lượng: {file ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : existingFiles[0]?.kich_thuoc_mb ? `${existingFiles[0]?.kich_thuoc_mb} MB` : "---"}
                        </p>
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          ℹ️ File định dạng Word sẽ được hệ thống xử lý và hiển thị trực tuyến khi xuất bản.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Chưa có tệp đính kèm</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Hủy
        </button>
        {!isEditMode && (
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Lưu nháp
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {isLoading && (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {isEditMode ? "Cập nhật" : "Tạo tài liệu"}
        </button>
      </div>

      <ConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => {
          setShowLeaveConfirm(false);
          onBack();
        }}
        onConfirm={() => setShowLeaveConfirm(false)}
        title="Rời khỏi trang tạo tài liệu"
        message="Bạn có chắc chắn muốn quay lại? Dữ liệu bạn vừa nhập chưa được lưu sẽ bị mất."
        confirmText="Ở lại"
        cancelText="Rời khỏi"
        type="warning"
        confirmType="warning"
      />
    </div>
  );
}
